import React, { useEffect, useRef, useState } from 'react';
import { Download, Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

type MenuKey = 'resources' | 'solutions';

interface NavLinkItem {
  label: string;
  to: string;
  description?: string;
}

const RESOURCE_LINKS: NavLinkItem[] = [
  { label: 'Blog', to: '/blog', description: 'Guías prácticas' },
  { label: 'Solucionar errores', to: '/recursos/errores', description: 'Diagnóstico por servicio' },
  { label: 'Guías', to: '/guias', description: 'Rutas por temática' },
  { label: 'Herramientas', to: '/herramientas', description: 'Utilidades operativas' },
  { label: 'Portales', to: '/portales', description: 'Sedes oficiales' },
  { label: 'Plantillas', to: '/plantillas', description: 'Recursos descargables' },
  { label: 'Glosario', to: '/glosario', description: 'Terminos clave' },
  { label: 'Documentación', to: '/docs', description: 'Uso del producto' }
];

const SOLUTION_LINKS: NavLinkItem[] = [
  { label: 'Todas las soluciones', to: '/soluciones', description: 'Capacidades y sectores' },
  { label: 'Software de Certy', to: '/software-gestion-certificados-digitales', description: 'Cómo funciona el producto' },
  { label: 'Buscar certificados', to: '/buscar-certificados-digitales-en-windows', description: 'Localizar por titular' },
  { label: 'Control de caducidad', to: '/control-caducidad-certificados-digitales', description: 'Vigencia y renovación' },
  { label: 'Acceso a portales', to: '/acceso-a-portales-oficiales-con-certificado-digital', description: 'AEAT, LexNET y más' },
  { label: 'Soluciones por sector', to: '/para/gestorias', description: 'Gestorías, abogados y equipos' }
];

const SOLUTION_LANDING_LINKS = [
  '/descargar-gestor-certificados-digitales',
  '/gestionar-varios-certificados-digitales',
  '/navegador-varios-certificados-digitales',
  '/importar-certificados-pfx-p12-masivamente',
  '/firma-pdf-con-certificado-digital',
  '/para/abogados',
  '/para/asesorias-fiscales',
  '/para/departamentos-laborales',
  '/para/administradores-de-fincas',
  '/para/notarias',
  '/para/pymes'
];

const HOVER_OPEN_DELAY_MS = 80;
const HOVER_CLOSE_DELAY_MS = 220;

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<MenuKey | null>(null);
  const [openMobileGroup, setOpenMobileGroup] = useState<MenuKey | null>(null);
  const desktopNavRef = useRef<HTMLDivElement | null>(null);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const isDownloads = pathname.replace(/\/$/, '') === '/downloads';

  const homeSectionHref = (sectionId: string) =>
    pathname === '/' ? `#${sectionId}` : `/#${sectionId}`;

  const isActivePath = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  const isPathInLinks = (links: NavLinkItem[]) => links.some((item) => isActivePath(item.to));
  const resourcesActive = isPathInLinks(RESOURCE_LINKS);
  const solutionsActive =
    isPathInLinks(SOLUTION_LINKS) || SOLUTION_LANDING_LINKS.some((route) => isActivePath(route));

  const desktopLinkClass = (active: boolean) =>
    [
      'text-sm font-medium transition-colors',
      active ? 'text-gray-900' : 'text-gray-600 hover:text-certy-green'
    ].join(' ');

  const desktopDropdownClass = (active: boolean, expanded: boolean) =>
    [
      'inline-flex items-center gap-1 text-sm font-medium transition-colors',
      active || expanded ? 'text-gray-900' : 'text-gray-600 hover:text-certy-green'
    ].join(' ');

  useEffect(() => {
    setOpenMenu(null);
    setMobileMenuOpen(false);
    setOpenMobileGroup(null);
  }, [pathname]);

  useEffect(() => {
    if (!openMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (desktopNavRef.current && !desktopNavRef.current.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenu]);

  const clearHoverTimers = () => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearHoverTimers();
  }, []);

  const handleDesktopDropdownEnter = (menu: MenuKey) => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (openMenu === menu) return;

    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
    }

    openTimerRef.current = window.setTimeout(() => {
      setOpenMenu(menu);
      openTimerRef.current = null;
    }, HOVER_OPEN_DELAY_MS);
  };

  const handleDesktopDropdownLeave = () => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }

    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }

    closeTimerRef.current = window.setTimeout(() => {
      setOpenMenu(null);
      closeTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  };

  const toggleDropdown = (menu: MenuKey) => {
    clearHoverTimers();
    setOpenMenu((previous) => (previous === menu ? null : menu));
  };

  const toggleMobileGroup = (menu: MenuKey) => {
    setOpenMobileGroup((previous) => (previous === menu ? null : menu));
  };

  const closeAllMenus = () => {
    clearHoverTimers();
    setOpenMenu(null);
    setMobileMenuOpen(false);
    setOpenMobileGroup(null);
  };

  const renderDesktopDropdown = (
    menu: MenuKey,
    label: string,
    links: NavLinkItem[],
    active: boolean
  ) => (
    <div
      className="relative"
      onMouseEnter={() => handleDesktopDropdownEnter(menu)}
      onMouseLeave={handleDesktopDropdownLeave}
    >
      <button
        type="button"
        aria-expanded={openMenu === menu}
        aria-label={`Abrir menu ${label}`}
        onClick={() => toggleDropdown(menu)}
        className={desktopDropdownClass(active, openMenu === menu)}
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${openMenu === menu ? 'rotate-180' : ''}`} />
      </button>

      {openMenu === menu && (
        <div className="absolute left-1/2 top-full z-50 mt-3 w-[360px] -translate-x-1/2 rounded-xl border border-gray-200 bg-white p-2 shadow-[0_18px_45px_rgba(15,23,42,0.15)]">
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-xs uppercase tracking-wide font-semibold text-gray-500">{label}</p>
          </div>
          {links.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpenMenu(null)}
              className="block rounded-lg px-3 py-3 hover:bg-gray-50 transition-colors"
            >
              <p className="text-sm font-semibold text-gray-900">{item.label}</p>
              {item.description && <p className="text-xs text-gray-500 mt-1">{item.description}</p>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );

  const renderMobileDropdown = (menu: MenuKey, label: string, links: NavLinkItem[]) => (
    <div className="border border-gray-200 rounded-xl">
      <button
        type="button"
        onClick={() => toggleMobileGroup(menu)}
        className="w-full px-4 py-3 flex items-center justify-between text-gray-900 font-semibold"
      >
        <span>{label}</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${openMobileGroup === menu ? 'rotate-180' : ''}`}
        />
      </button>

      {openMobileGroup === menu && (
        <div className="px-4 pb-4 space-y-1">
          {links.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="block rounded-lg px-2 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={closeAllMenus}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <header className="bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 relative">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img src="/certy-logo-96.png" alt="Certy" width="40" height="40" className="w-10 h-10" />
            <span className="text-2xl font-bold text-gray-900">Certy</span>
          </Link>

          <nav ref={desktopNavRef} className="hidden lg:flex items-center gap-7">
            {isDownloads ? (
              <>
                <a href="#instalacion" className={desktopLinkClass(false)}>Instalación</a>
                <a href="#licencia" className={desktopLinkClass(false)}>Licencia</a>
                <a href="#preguntas-descarga" className={desktopLinkClass(false)}>Ayuda</a>
              </>
            ) : isHome ? (
              <>
                <a href="#features" className={desktopLinkClass(false)}>Producto</a>
                <a href="#workflow" className={desktopLinkClass(false)}>Cómo funciona</a>
                <a href="#pricing" className={desktopLinkClass(false)}>Precios</a>
                <a href="#faq" className={desktopLinkClass(false)}>Preguntas</a>
              </>
            ) : (
              <>
                <a href={homeSectionHref('features')} className={desktopLinkClass(false)}>Producto</a>
                {renderDesktopDropdown('resources', 'Recursos', RESOURCE_LINKS, resourcesActive)}
                {renderDesktopDropdown('solutions', 'Soluciones', SOLUTION_LINKS, solutionsActive)}
                <a href={homeSectionHref('pricing')} className={desktopLinkClass(false)}>Precios</a>
                <a href={homeSectionHref('contact')} className={desktopLinkClass(false)}>Contacto</a>
              </>
            )}
          </nav>

          <Link
            to="/downloads"
            className="hidden sm:flex bg-black text-white px-4 py-2 text-sm md:px-6 md:py-3 md:text-base rounded-none border-2 border-black hover:bg-certy-green hover:border-transparent hover:text-white transition-all duration-200 items-center space-x-2 font-medium ml-2"
          >
            <span>
              Descargar
            </span>
            <Download className="w-4 h-4" />
          </Link>

          <button
            className="lg:hidden ml-2 p-2 rounded focus:outline-none focus:ring-2 focus:ring-certy-green"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Abrir menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {mobileMenuOpen && (
            <div className="absolute top-full left-0 w-full bg-white shadow-lg border-b border-gray-100 py-6 px-6 lg:hidden z-50">
              <div className="space-y-6">
                {isDownloads ? (
                  <div className="space-y-1">
                    {[
                      ['Instalación', '#instalacion'],
                      ['Licencia', '#licencia'],
                      ['Ayuda', '#preguntas-descarga'],
                    ].map(([label, href]) => (
                      <a key={href} href={href} className="block rounded-lg px-3 py-3 font-medium text-gray-800 hover:bg-gray-50" onClick={closeAllMenus}>{label}</a>
                    ))}
                  </div>
                ) : isHome ? (
                  <div className="space-y-1">
                    {[
                      ['Producto', '#features'],
                      ['Cómo funciona', '#workflow'],
                      ['Precios', '#pricing'],
                      ['Preguntas', '#faq'],
                    ].map(([label, href]) => (
                      <a key={href} href={href} className="block rounded-lg px-3 py-3 font-medium text-gray-800 hover:bg-gray-50" onClick={closeAllMenus}>{label}</a>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="border border-gray-200 rounded-xl p-4 space-y-2">
                      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Producto</p>
                      <a href={homeSectionHref('features')} className="block text-gray-800 font-medium rounded-lg px-2 py-2 hover:bg-gray-50" onClick={closeAllMenus}>Características</a>
                      <a href={homeSectionHref('pricing')} className="block text-gray-800 font-medium rounded-lg px-2 py-2 hover:bg-gray-50" onClick={closeAllMenus}>Precios</a>
                      <a href={homeSectionHref('contact')} className="block text-gray-800 font-medium rounded-lg px-2 py-2 hover:bg-gray-50" onClick={closeAllMenus}>Contacto</a>
                    </div>
                    {renderMobileDropdown('resources', 'Recursos', RESOURCE_LINKS)}
                    {renderMobileDropdown('solutions', 'Soluciones', SOLUTION_LINKS)}
                  </>
                )}

                <Link
                  to="/downloads"
                  className="sm:hidden w-full bg-black text-white px-4 py-3 text-sm rounded-none border-2 border-black hover:bg-certy-green hover:border-transparent transition-all duration-200 inline-flex items-center justify-center gap-2 font-medium"
                  onClick={closeAllMenus}
                >
                  Descargar
                  <Download className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
